import { useSyncExternalStore } from 'react';

/**
 * The two things about a drop that the queue cannot learn from the task list.
 *
 * Kept outside React because the two sides never meet in the tree: a drop is handled wherever it
 * lands — any composer mount point, the onboarding upload box — and the queue is mounted once in
 * `GlobalOverlays`.
 */
let dropsLanded = 0;
let walksInProgress = 0;
const listeners = new Set<() => void>();

const announce = (): void => {
	for (const listener of listeners) listener();
};

/**
 * A drop is being walked. Reading the dropped tree is the one stretch of a drop with nothing on
 * screen — no file list exists yet, so there is nothing to build a row from — and on a cloud-synced
 * folder it lasts long enough to read as the drop having been ignored.
 *
 * Counted rather than flagged: two drops can be walked at once, and the first to finish must not
 * clear the second's state.
 */
export const signalWalkStarted = (): void => {
	walksInProgress += 1;
	announce();
};

export const signalWalkFinished = (): void => {
	walksInProgress = Math.max(0, walksInProgress - 1);
	announce();
};

/**
 * What the last drop carried and what it scheduled. A drop has to answer for itself even when it
 * schedules no work — re-dropping a folder that has already finished is ordinary — and the answer
 * has to be about *that* drop. Reporting only the batch left a four-file drop reading `53 of 53
 * complete`, which is true of something the user did not just do.
 *
 * `at` increments on every drop so the queue can tell two identical drops apart, and the object is
 * replaced only when a drop lands, which is what `useSyncExternalStore` needs from a snapshot.
 */
export interface DropReceipt {
	/** Files the drop carried, after the OS's own bookkeeping files were excluded. */
	filesDropped: number;
	/** How many of those became tasks. Zero means every file already had an answer. */
	scheduled: number;
	/** When it landed, so the queue can tell whether anything has happened since. */
	landedAt: number;
	at: number;
}

let lastDrop: DropReceipt = { filesDropped: 0, scheduled: 0, landedAt: 0, at: 0 };

export const signalDropLanded = (filesDropped: number, scheduled: number): void => {
	dropsLanded += 1;
	lastDrop = { filesDropped, scheduled, landedAt: Date.now(), at: dropsLanded };
	announce();
};

const subscribe = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const getLastDrop = (): DropReceipt => lastDrop;
const getIsReadingDrop = (): boolean => walksInProgress > 0;

export const useLastDrop = (): DropReceipt => useSyncExternalStore(subscribe, getLastDrop, getLastDrop);

export const useIsReadingDrop = (): boolean => useSyncExternalStore(subscribe, getIsReadingDrop, getIsReadingDrop);

/** Module state outlives a test, so each one starts from a page that has received no drops. */
export const forgetDropSignals = (): void => {
	dropsLanded = 0;
	walksInProgress = 0;
	lastDrop = { filesDropped: 0, scheduled: 0, landedAt: 0, at: 0 };
};
